import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Crown, ShieldCheck, AlertTriangle, ArrowRight, ExternalLink,
  Loader2, CheckCircle2, Clock, XCircle, Sparkles, Wallet,
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';
import { stripeDashboardLink, stripeRefresh } from '../api/payouts';

/**
 * VipCreatorPanel — Phase 13 control panel that lives on /profile.
 *
 *  - For non-VIPs (no Stripe Connect account yet): big "Become a VIP Creator" CTA
 *  - For in-progress onboarding: "Resume Stripe onboarding" button
 *  - For active VIPs: status grid + "Manage Stripe account" + "Creator Dashboard"
 *  - For admin-suspended monetization: red banner explaining why and pointing to support
 */
const VipCreatorPanel = () => {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('lives');
  const [busyKey, setBusyKey] = useState(null);

  if (!user) return null;

  const hasAccount = !!user.stripe_connect_id;
  // Phase 90 — VIP status is decoupled from Stripe:
  //   isActiveVip → creator_status === 'active' (can sell + earn Credits)
  //   stripeReady → Stripe payout onboarding completed (can withdraw)
  const isActiveVip = hasActiveCreatorProfile(user);
  const stripeReady = !!user.is_payout_ready;
  const allReady = stripeReady;
  const monetizationOk = user.monetization_enabled !== false;
  const inProgress = hasAccount && !stripeReady;
  const isSuspended = isActiveVip && !monetizationOk;
  const needsCompletion = isActiveVip && (!user.phone_verified || !user.creator_agreement_accepted);

  // Stripe stage label for the status grid
  let stripeStage = t('vip_panel.stage_incomplete');
  let stripeStageColor = 'bg-amber-500/20 gold-text';
  let StripeStageIcon = AlertTriangle;
  if (!hasAccount) {
    stripeStage = t('vip_panel.stage_not_started');
    stripeStageColor = 'bg-muted text-muted-foreground';
    StripeStageIcon = XCircle;
  } else if (inProgress) {
    stripeStage = t('vip_panel.stage_in_verification');
    stripeStageColor = 'bg-amber-500/20 gold-text';
    StripeStageIcon = Clock;
  } else if (stripeReady) {
    stripeStage = t('vip_panel.stage_active');
    stripeStageColor = 'bg-emerald-500/20 text-emerald-300';
    StripeStageIcon = CheckCircle2;
  }

  // Monetization label — Phase 90: keyed on VIP activation, not Stripe.
  const monetLabel = !isActiveVip
    ? { text: t('vip_panel.monet_locked'), color: 'bg-muted text-muted-foreground', Icon: XCircle }
    : isSuspended
      ? { text: t('vip_panel.monet_suspended'), color: 'bg-red-500/20 text-red-300', Icon: AlertTriangle }
      : { text: t('vip_panel.monet_active'), color: 'bg-emerald-500/20 text-emerald-300', Icon: CheckCircle2 };

  const startOnboarding = async () => {
    // Phase 43 · Feb 2026 — CTA "Diventa VIP Creator" ora apre il nuovo
    // Wizard di Onboarding Creator (multi-step) al posto del vecchio
    // Dialog PayoutOnboardingCard. Il wizard gestisce raccolta dati
    // fiscali → riepilogo → Stripe Express in un flusso unificato,
    // mobile-first, con salvataggio automatico e resume.
    navigate('/creator/onboarding');
  };

  const openDashboard = async () => {
    setBusyKey('dashboard');
    try {
      const data = await stripeDashboardLink();
      if (data.mock) {
        toast({
          title: t('vip_panel.mock_mode_title'),
          description: t('vip_panel.mock_mode_desc'),
        });
        return;
      }
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast({
        title: t('vip_panel.dashboard_open_error'),
        description: e.response?.data?.detail || t('vip_panel.dashboard_open_error_desc'),
        variant: 'destructive',
      });
    } finally { setBusyKey(null); }
  };

  const refreshStatus = async () => {
    setBusyKey('refresh');
    try {
      await stripeRefresh();
      await refresh();
      toast({ title: t('vip_panel.status_refreshed') });
    } catch (_e) {
      toast({ title: t('vip_panel.status_refresh_failed'), variant: 'destructive' });
    } finally { setBusyKey(null); }
  };

  // ============ Variant A: non-VIP nudge ============
  // Phase 90 — shown to every user who hasn't activated the creator
  // profile yet (fiscal data + phone OTP + agreement), regardless of any
  // legacy Stripe account: the wizard is the single entry point.
  if (!isActiveVip) {
    return (
      <>
        <div
          className="mt-6 overflow-hidden rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-card to-card"
          data-testid="become-vip-card"
        >
          <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                <Crown className="h-6 w-6 gold-text" />
              </div>
              <div>
                <p className="text-base font-semibold gold-text">{t('vip_panel.become_vip_title')}</p>
                <p className="mt-0.5 max-w-md text-sm text-muted-foreground">
                  {t('vip_panel.become_vip_desc')}
                </p>
              </div>
            </div>
            <Button
              onClick={startOnboarding}
              disabled={busyKey === 'onboard'}
              className="h-10 gold-gradient text-sm font-semibold shadow-md"
              data-testid="become-vip-btn"
            >
              {busyKey === 'onboard'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : (<><Sparkles className="mr-2 h-4 w-4" /> {t('vip_panel.become_vip_cta')} <ArrowRight className="ml-2 h-4 w-4" /></>)
              }
            </Button>
          </div>
        </div>
      </>
    );
  }

  // ============ Variant B/C: in progress OR active ============
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="vip-management-panel">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 gold-text" />
          <h2 className="text-sm font-semibold uppercase tracking-wide gold-text">{t('vip_panel.management_title')}</h2>
        </div>
        <button
          onClick={refreshStatus}
          disabled={busyKey === 'refresh'}
          className="text-xs text-muted-foreground hover:gold-text disabled:opacity-50"
          data-testid="vip-refresh-status-btn"
        >
          {busyKey === 'refresh' ? t('vip_panel.refreshing') : t('vip_panel.refresh')}
        </button>
      </div>

      {/* Phase 90 — migrated active creators missing phone OTP / agreement:
          gentle but explicit completion banner (non-blocking: they keep
          selling in the meantime). */}
      {needsCompletion && (
        <div
          className="mb-4 flex flex-col gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
          data-testid="vip-completion-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
              <ShieldCheck className="h-4 w-4 gold-text" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold gold-text">{t('vip_panel.completion_title')}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('vip_panel.completion_desc')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/creator/onboarding')}
            className="ml-12 h-9 self-start gold-gradient text-sm font-semibold"
            data-testid="vip-completion-cta"
          >
            {t('vip_panel.completion_cta')} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Phase 2D · Fase 8 — Banner visibile per creator che hanno iniziato
          Stripe Express ma non hanno completato l'onboarding. Prompt gentile
          ma inequivocabile con CTA "Riprendi verifica" che riapre il Dialog
          Stripe Express (stesso flusso di Variant A). Zero rischio: se il
          creator è già completo (allReady), il banner non appare.

          Phase 2D · Fase 9 — Checklist a 3 passaggi invece di percentuale:
          - Account Stripe: sempre ✅ in questo branch (ha stripe_connect_id)
          - Identità verificata: `stripe_connect_charges_enabled` (Stripe attiva
            i charges dopo il completamento della verifica documenti)
          - Conto bancario: `stripe_connect_payouts_enabled` (i payout si
            attivano solo dopo che Stripe conferma il conto bancario)
          Trasparente, no percentuale fuorviante, coerente con lo stato che
          Stripe stesso ci comunica via webhook. */}
      {inProgress && (() => {
        const chargesOk = !!user?.stripe_connect_charges_enabled;
        const payoutsOk = !!user?.stripe_connect_payouts_enabled;
        const stepsDone = 1 + (chargesOk ? 1 : 0) + (payoutsOk ? 1 : 0);
        const remaining = 3 - stepsDone;
        return (
          <div
            className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4"
            data-testid="vip-onboarding-pending-banner"
          >
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                <Clock className="h-4 w-4 gold-text" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold gold-text" data-testid="vip-pending-title">
                  {t('vip_panel.onboarding_pending_title')}
                </p>
                <p className="mt-0.5 text-[11px] font-mono text-muted-foreground" data-testid="vip-pending-progress">
                  {t('vip_panel.onboarding_progress', { done: stepsDone, total: 3 })}
                </p>
              </div>
            </div>

            {/* Checklist 3-step. Ogni riga usa ✅ per completato, ⏳ per pendente. */}
            <ul className="space-y-1 pl-12 text-[12px]" data-testid="vip-pending-checklist">
              <li className="flex items-center gap-2" data-testid="vip-step-account">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                <span className="text-foreground">{t('vip_panel.step_account_created')}</span>
              </li>
              <li className="flex items-center gap-2" data-testid="vip-step-identity">
                {chargesOk
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                  : <Clock className="h-4 w-4 shrink-0 gold-text" />}
                <span className={chargesOk ? 'text-foreground' : 'text-muted-foreground'}>
                  {t('vip_panel.step_identity_verified')}
                </span>
              </li>
              <li className="flex items-center gap-2" data-testid="vip-step-bank">
                {payoutsOk
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                  : <Clock className="h-4 w-4 shrink-0 gold-text" />}
                <span className={payoutsOk ? 'text-foreground' : 'text-muted-foreground'}>
                  {t('vip_panel.step_bank_confirmed')}
                </span>
              </li>
            </ul>

            <p className="pl-12 text-[11px] text-muted-foreground" data-testid="vip-pending-remaining">
              {remaining === 1
                ? t('vip_panel.onboarding_remaining_one')
                : t('vip_panel.onboarding_remaining_many', { count: remaining })}
            </p>

            <Button
              onClick={() => navigate('/lives?tab=earnings')}
              className="ml-12 h-9 self-start gold-gradient text-sm font-semibold shadow-md"
              data-testid="vip-onboarding-pending-cta"
            >
              <ShieldCheck className="mr-1.5 h-4 w-4" /> {t('vip_panel.onboarding_pending_cta')} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        );
      })()}

      {/* Status grid */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatusTile
          label={t('vip_panel.tile_stripe_account')}
          value={stripeStage}
          color={stripeStageColor}
          Icon={StripeStageIcon}
          testid="vip-stripe-stage"
        />
        <StatusTile
          label={t('vip_panel.tile_pending')}
          value={inProgress ? t('vip_panel.pending_awaiting') : t('vip_panel.pending_none')}
          color={inProgress ? 'bg-amber-500/20 gold-text' : 'bg-emerald-500/20 text-emerald-300'}
          Icon={inProgress ? Clock : CheckCircle2}
          testid="vip-pending-checks"
        />
        <StatusTile
          label={t('vip_panel.tile_monetization')}
          value={monetLabel.text}
          color={monetLabel.color}
          Icon={monetLabel.Icon}
          testid="vip-monetization-status"
        />
      </div>

      {/* Suspended banner */}
      {isSuspended && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-300" />
          <div className="text-xs leading-snug text-red-200">
            {t('vip_panel.suspended_banner')}
          </div>
        </div>
      )}

      {/* Dynamic CTAs */}
      <div className="flex flex-wrap items-center gap-2">
        {!stripeReady && (
          <Button
            onClick={() => navigate('/lives?tab=earnings')}
            className="h-9 gold-gradient text-sm font-semibold"
            data-testid="vip-receive-earnings-btn"
          >
            <Wallet className="mr-1.5 h-4 w-4" /> {t('vip_panel.receive_earnings_cta')}
          </Button>
        )}
        {allReady && (
          <>
            <Button
              onClick={openDashboard}
              disabled={busyKey === 'dashboard'}
              variant="outline"
              className="h-9 border-amber-500/40 text-sm font-semibold gold-text hover:bg-amber-500/10"
              data-testid="vip-manage-stripe-btn"
            >
              {busyKey === 'dashboard'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : (<><ExternalLink className="mr-1.5 h-4 w-4" /> {t('vip_panel.manage_stripe')}</>)
              }
            </Button>
            <Button
              onClick={() => navigate('/lives?tab=earnings')}
              className="h-9 bg-foreground text-background text-sm font-semibold hover:opacity-90"
              data-testid="vip-creator-dashboard-btn"
            >
              <Wallet className="mr-1.5 h-4 w-4" /> {t('vip_panel.creator_dashboard')}
            </Button>
          </>
        )}
      </div>

      {hasAccount && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          {t('vip_panel.stripe_account_id')}: <code className="rounded bg-muted px-1.5 py-0.5">{user.stripe_connect_id}</code>
        </p>
      )}
    </div>
  );
};

const StatusTile = ({ label, value, color, Icon, testid }) => (
  <div className="rounded-xl border border-border bg-background p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <div className="mt-2 flex items-center gap-2">
      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${color}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  </div>
);

export default VipCreatorPanel;
